import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { Check, ArrowRight } from 'lucide-react';
import PageShell from '@/components/PageShell';
import { Button } from '@/components/ui/button';
import PhotoStrip, { galleryImages as g } from '@/components/PhotoStrip';

const slugImages: Record<string, { src: string; alt: string }[]> = {
  'automotive-restoration': [g.carb1, g.head, g.mikuni, g.fins],
  'industrial-cleaning': [g.gearbox, g.detail, g.holley, g.cases],
  'marine-cleaning': [g.hub, g.carb2, g.head, g.detail],
};

interface ServiceContent {
  slug: string;
  title: string;
  metaTitle: string;
  metaDescription: string;
  hero: string;
  intro: string;
  bullets: string[];
  body: string[];
  idealFor: string[];
}

const services: Record<string, ServiceContent> = {
  'automotive-restoration': {
    slug: 'automotive-restoration',
    title: 'Automotive Restoration',
    metaTitle:
      'Auto Restoration Christchurch | Car & Motorcycle Vapour Blasting',
    metaDescription:
      'Christchurch specialists in engine cases, carburettors, alloy wheels and classic restorations. Vapour blasting restores parts to a satin factory finish without damage.',
    hero: 'Bring engine parts back to factory finish',
    intro:
      'Classic motorcycles, sports cars, vintage restorations and modern alloy components — vapour blasting strips decades of corrosion, baked-on oil and oxidation without removing measurable material or harming threads, sealing surfaces or machined faces.',
    bullets: [
      'Engine cases, crankcases and cam covers',
      'Cylinder heads, barrels and rocker covers',
      'Carburettors, throttle bodies and intakes',
      'Alloy wheels, brake calipers and hubs',
      'Fork legs, yokes and triple clamps',
      'Vintage and classic restoration parts',
    ],
    body: [
      "Most automotive customers come to us with parts pulled from a project that's been sitting for years. The aluminium has gone chalky, oil residue is baked into corners, and dry blasting risks distorting thin castings or pitting the surface. Vapour blasting solves this — fine media is suspended in water and propelled by compressed air, so the abrasive skims the surface rather than hammering it.",
      "The finish is a uniform satin that looks factory-new without the over-bright 'just-blasted' look that dries out and oxidises within days. For show parts or anything you want to keep looking sharp long-term, we recommend a clear coat or Renaissance Wax after blasting.",
      'We work with restorers across Canterbury — vintage Triumph and Norton, Japanese classics like the GS and CB750, European exotica and modern performance cars. Drop parts off in Islington or ship them to us from anywhere in New Zealand.',
    ],
    idealFor: [
      'Classic motorcycle restorations',
      'Vintage and project car rebuilds',
      'Pre-paint preparation for alloy parts',
      'Race teams turning around fresh components',
    ],
  },
  'industrial-cleaning': {
    slug: 'industrial-cleaning',
    title: 'Industrial Cleaning',
    metaTitle: 'Industrial Vapour Blasting Christchurch | Tooling & Equipment',
    metaDescription:
      'Vapour blasting for industrial equipment, tooling, moulds and architectural metalwork in Christchurch. Remove rust, paint and scale without damaging precision surfaces.',
    hero: 'Production-grade cleaning without surface damage',
    intro:
      'For workshops, manufacturers and trades, vapour blasting is the cleanest way to refurbish jigs, tooling, moulds and assembled hardware without dust, abrasive embedding or measurable material loss.',
    bullets: [
      'Production jigs, fixtures and tooling',
      'Injection mould tools and inserts',
      'Stainless steel architectural fittings',
      'Brass and bronze hardware',
      'Heat exchangers and pump components',
      'Powder-coat and paint stripping prep',
    ],
    body: [
      'Industrial customers value vapour blasting for three reasons: no airborne dust, no media embedding into the substrate, and consistent results across batches. The wet process keeps respiratory hazards down and means parts come out clean and ready to handle.',
      'We can match the finish to your spec — from heavy decontamination of cast iron and steel to fine-finish preparation of stainless and aluminium ahead of welding, anodising or powder coating. Larger batches and recurring jobs are welcome; talk to us about scheduled turnaround.',
      'Based in Islington, Christchurch, we serve fabricators, mould shops and manufacturing teams across Canterbury including Hornby, Sockburn, Rolleston and the wider South Island.',
    ],
    idealFor: [
      'Fabricators preparing parts for finishing',
      'Mould and tooling refurbishment',
      'Recurring batch decontamination',
      'Architectural and hospitality fittings',
    ],
  },
  'marine-cleaning': {
    slug: 'marine-cleaning',
    title: 'Marine Cleaning',
    metaTitle: 'Marine Vapour Blasting Christchurch | Props & Salt Corrosion',
    metaDescription:
      'Vapour blasting for marine parts in Christchurch & Canterbury — propellers, outboard components, stainless fittings and hardware. Remove salt corrosion without pitting.',
    hero: 'Strip salt corrosion without pitting the metal',
    intro:
      'Saltwater is brutal on aluminium outboard parts, bronze props and stainless deck hardware. Vapour blasting cuts through chloride corrosion and oxidation without the surface pitting you get from aggressive dry blasting — critical for parts that need to seal or stay hydrodynamic.',
    bullets: [
      'Outboard engine cases and powerheads',
      'Bronze and stainless propellers',
      'Stainless and aluminium deck fittings',
      'Saltwater pump and impeller housings',
      'Trailer hardware and winches',
      'Pre-paint and pre-anodise preparation',
    ],
    body: [
      "Marine corrosion typically sits in a thick chalky layer over the parent metal. Sand or grit blasting will get it off but leaves the surface rough and may distort thinner castings. Vapour blasting strips the corrosion with the abrasive cushioned by water — you end up with a smooth, even finish that's ready for clear coating, anodising or reassembly.",
      'Common projects: outboard powerheads brought back to life after a season of neglect, bronze props refreshed for race weekends, stainless deck hardware on classic launches. We also handle pre-paint preparation for hull fittings.',
      'Customers ship parts to our Islington workshop from across Canterbury and the South Island, including Lyttelton, Akaroa, Picton and beyond.',
    ],
    idealFor: [
      'Outboard refurbishment',
      'Propeller cleaning and tune-up',
      'Classic launch restoration',
      'Saltwater hardware refresh',
    ],
  },
};

const ServiceDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const service = slug ? services[slug] : undefined;

  if (!service) return <Navigate to="/services" replace />;

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: service.title,
    description: service.metaDescription,
    provider: {
      '@type': 'LocalBusiness',
      name: 'Precision Vapour Blasting',
      '@id': 'https://precisionvapour.co.nz/#business',
    },
    areaServed: { '@type': 'State', name: 'Canterbury, New Zealand' },
    url: `https://precisionvapour.co.nz/services/${service.slug}`,
  };

  return (
    <PageShell
      title={service.metaTitle}
      description={service.metaDescription}
      canonicalPath={`/services/${service.slug}`}
      crumbs={[
        { name: 'Services', path: '/services' },
        { name: service.title, path: `/services/${service.slug}` },
      ]}
      jsonLd={[serviceSchema]}
    >
      <section className="section-padding bg-background">
        <div className="container-wide mx-auto max-w-4xl">
          <p className="text-primary font-medium tracking-[0.3em] uppercase mb-4 text-sm">
            {service.title}
          </p>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-foreground">
            {service.hero}
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground mb-10">{service.intro}</p>

          <div className="glass-card rounded-xl p-8 mb-12">
            <h2 className="font-display text-2xl font-bold mb-6 text-foreground">
              What we restore
            </h2>
            <ul className="grid sm:grid-cols-2 gap-3">
              {service.bullets.map((b) => (
                <li key={b} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="text-primary w-3.5 h-3.5" strokeWidth={3} />
                  </div>
                  <span className="text-foreground">{b}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="space-y-6 mb-12">
            {service.body.map((p, i) => (
              <p key={i} className="text-lg text-muted-foreground leading-relaxed">
                {p}
              </p>
            ))}
          </div>

          <div className="glass-card rounded-xl p-8 mb-12">
            <h2 className="font-display text-2xl font-bold mb-4 text-foreground">Ideal for</h2>
            <ul className="space-y-2">
              {service.idealFor.map((b) => (
                <li key={b} className="flex items-center gap-3 text-foreground">
                  <ArrowRight className="w-4 h-4 text-primary" />
                  {b}
                </li>
              ))}
            </ul>
          </div>

          {slugImages[service.slug] && (
            <PhotoStrip
              heading={`${service.title} — finished examples`}
              blurb="Recent parts restored in our Christchurch workshop."
              images={slugImages[service.slug]}
            />
          )}

          <div className="text-center">
            <Button variant="hero" size="xl" asChild>
              <Link to="/contact">Get a free quote</Link>
            </Button>

            <p className="text-sm text-muted-foreground mt-4">
              Or read more about{' '}
              <Link to="/process" className="text-primary hover:underline">
                how the process works
              </Link>
              .
            </p>
          </div>
        </div>
      </section>
    </PageShell>
  );
};

export default ServiceDetail;
